/**
 * WHAT A CLASS ACTUALLY STARTS WITH.
 *
 * A mirror of `BASE_STATS` in `backend/src/game/heroes/heroes.service.ts` — the
 * numbers a hero is genuinely created with. It lives here because three
 * separate surfaces advertise a class before the player owns one (the landing
 * warband cards, the guide's warband section, the in-game naming stones) and
 * all three used to invent their own vocabulary: the landing showed
 * Might/Cunning/Wyrd, the picker showed Might/Wit/Dread, and neither set of
 * numbers had anything to do with the hero you got (QA 2026-08-18). There is
 * exactly one table now, and it speaks the same four stats as the guide.
 *
 * Keeping a copy client-side rather than fetching it is deliberate: the landing
 * is a pre-login marketing page and must render with no API round-trip. The
 * backend pins these values in `heroes.service.test.ts`, so a balance change
 * there fails a test that names this file.
 */
export type ClassStatKey = 'attack' | 'defense' | 'magic' | 'speed' | 'hp';

export const CLASS_BASE_STATS: Record<
  'WARRIOR' | 'PALADIN' | 'ARCHER' | 'MAGE',
  Record<ClassStatKey, number>
> = {
  WARRIOR: { attack: 18, defense: 15, magic: 4, speed: 8, hp: 120 }, // Berserker
  PALADIN: { attack: 14, defense: 14, magic: 10, speed: 9, hp: 110 }, // Huskarl
  ARCHER: { attack: 20, defense: 8, magic: 6, speed: 16, hp: 90 }, // Hunter
  MAGE: { attack: 8, defense: 7, magic: 22, speed: 11, hp: 80 }, // Völva
};

/** Row order on every class card — the guide's four stats, then the life pool. */
export const CLASS_STAT_ROWS: readonly ClassStatKey[] = ['attack', 'defense', 'magic', 'speed', 'hp'];

/** Highest value any class carries in a given stat — the top of that row's bar. */
export function classStatMax(key: ClassStatKey): number {
  return Math.max(...Object.values(CLASS_BASE_STATS).map((s) => s[key]));
}

/**
 * Cut marks to light, out of `pips`, for one stat of one class.
 *
 * Normalised per row (against that stat's own best) rather than across all
 * stats: Cunning tops out at 22 and Toughness at 15, so a shared scale would
 * draw every Toughness bar short and read as a nerf that isn't there. Rounded
 * UP so a non-zero stat never shows an empty bar — the exact figure is printed
 * beside the bar anyway, and it is the figure that has to be true.
 */
export function classStatPips(key: ClassStatKey, value: number, pips = 5): number {
  if (value <= 0) return 0;
  return Math.min(pips, Math.max(1, Math.ceil((value / classStatMax(key)) * pips)));
}

/** i18n key for a stat's player-facing name — the same catalogue the gear UI reads. */
export const CLASS_STAT_LABEL_KEY: Record<ClassStatKey, string> = {
  attack: 'items.stats.firepower',
  defense: 'items.stats.toughness',
  magic: 'items.stats.cunning',
  speed: 'items.stats.reflexes',
  hp: 'items.stats.health',
};
